import React, { useState } from 'react';
import { Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { trySetServiceUnavailableError } from '../../utils/serviceUnavailableMessages';

const ResetPassword = ({ token, onSuccess, onSwitchToLogin }) => {
  const { confirmPasswordReset } = useAuth();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    password: '',
    password_confirm: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.password !== formData.password_confirm) {
      setError(t('resetPassword.passwordsDoNotMatch'));
      setLoading(false);
      return;
    }

    try {
      await confirmPasswordReset(token, formData.password, formData.password_confirm);
      onSuccess();
    } catch (err) {
      if (trySetServiceUnavailableError(err, setError, t)) return;
      const errorText = err.data?.errorText;
      if (typeof errorText === 'string') {
        setError(errorText);
      } else if (typeof errorText === 'object') {
        const messages = Object.entries(errorText)
          .map(([field, msgs]) => {
            const fieldName = field.replace(/_/g, ' ');
            const msgList = Array.isArray(msgs) ? msgs : [msgs];
            return msgList.map(m => `${fieldName}: ${m}`);
          })
          .flat();
        setError(messages.join('. '));
      } else {
        setError(t('resetPassword.failed'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="auth-card" style={{ maxWidth: '420px', width: '100%' }}>
      <Card.Body className="p-4 p-md-5">
        <div className="text-center mb-4">
          <div className="icon-circle bg-primary bg-gradient mx-auto mb-3">
            <i className="bi bi-key text-white"></i>
          </div>
          <h2 className="fw-bold">{t('resetPassword.title')}</h2>
          <p className="text-muted">{t('resetPassword.subtitle')}</p>
        </div>

        {error && <Alert variant="danger">{error}</Alert>}

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>{t('resetPassword.newPasswordLabel')}</Form.Label>
            <div className="position-relative">
              <Form.Control
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder={t('resetPassword.newPasswordPlaceholder')}
                required
                minLength={8}
                autoComplete="new-password"
                size="lg"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                <i className={`bi bi-eye${showPassword ? '-slash' : ''}`}></i>
              </button>
            </div>
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label>{t('resetPassword.confirmPasswordLabel')}</Form.Label>
            <Form.Control
              type="password"
              name="password_confirm"
              value={formData.password_confirm}
              onChange={handleChange}
              placeholder={t('resetPassword.confirmPasswordPlaceholder')}
              required
              minLength={8}
              autoComplete="new-password"
              size="lg"
            />
          </Form.Group>

          <Button
            type="submit"
            className="btn-gradient w-100 py-2"
            size="lg"
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {t('resetPassword.resetting')}
              </>
            ) : (
              t('resetPassword.resetButton')
            )}
          </Button>
        </Form>

        <hr className="my-4" />

        <div className="text-center">
          <Button variant="link" className="p-0 link-primary" onClick={onSwitchToLogin}>
            <i className="bi bi-arrow-left me-2"></i>
            {t('resetPassword.backToLogin')}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};

export default ResetPassword;
